//@flow
import TrpcLibrary from 'digicel-kaizen-library/lib/tripica-library';
import I18n from '../../../lib/i18n';
import { formatValue } from '../../../lib/usage-formatters';
import config from '../../../config';
import extractRoamingZone from '../../../lib/RoamingZoneExtractor';
import { extractIddZone } from '../../../lib/IddZoneExtractor';
import formatDuration from '../../../lib/usage-formatters/duration-usage-formatter';
import formatSms from '../../../lib/usage-formatters/sms-usage-formatter';
import formatVolume from '../../../lib/usage-formatters/volume-usage-formatter';
import getUnit from '../../../lib/TripicaUnitTypeFormatter';
import WizzeeLog from '../../../lib/wizzeeLog';

type PaymentDetail = {
  name: string,
  type: string,
  value: string | number,
  unit: ?string,
};

const getPaymentType = (type: ?string) => {
  if (!type) return null;
  if (extractRoamingZone(type)) return 'ROAMING';
  return type;
};

const getUnitType = (unit: ?string) => {
  if (!unit) return null;
  let unitType = getUnit(unit);

  // Ugly workaround to map ROA_DATA unit type to the more generic DATA type used in Tripica's SDK for unit mapping.
  // Tripica SDK doesn't recognize ROA_DATA as a unit type, but we need to keep the ROA_DATA type in our codebase to 
  // identify roaming data usage details and apply specific formatting to them. By mapping ROA_DATA to DATA when retrieving 
  // the unit mapping from Tripica, we can have the best of both worlds: specific handling for ROA_DATA in our code,
  // and a valid unit type for Tripica's SDK. Will ask Tripica to support ROA_DATA as a unit type in the future to remove this workaround.
  if (unitType && unitType === 'ROA_DATA') {
    unitType = 'DATA';
  }
  return TrpcLibrary.getUnitMapping(unitType);
};

const getPaymentName = (name: ?string) => {
  if (!name) return null;
  if (name.includes('GSTREBATE')) return 'GSTREBATE';
  if (name.includes('PROMO')) return 'PROMO';
  return name;
};

export const getBoosterZone = (name: ?string, boosterType: string) => {
  if (name && name.includes(boosterType)) {
    const zone: ?string = name.split('_').find(word => word.includes(boosterType));
    if (zone) {
      return zone.substr(zone.length - 1);
    }
  }
  return null;
};

export const getProductFromNameOrType = (paidItemName: ?string, paidItemType: ?string) => {
  const name = getPaymentName(paidItemName);
  const type = getPaymentType(paidItemType);
  return config.products[type] || config.products[name];
};

export const getPaymentDetailText = (paymentDetail: PaymentDetail) => {
  const { name, type, value } = paymentDetail;

  const forbiddenProducts = [/.*_SIM$/,/.*ESIM$/, /.*_PLN$/, /.*_PREMIUM_USAGES$/, /.*_BONUS_PROMO$/];
  const isForbiddenProduct = forbiddenProducts.some(forbiddenProduct => forbiddenProduct.test(name));
  if (isForbiddenProduct) return;

  if (
    name === 'KAIZEN_PLN' ||
    name === 'KAIZEN_SIM' ||
    name === 'KAIZEN_PREMIUM_USAGES' ||
    name === 'KAIZEN_BONUS_PROMO'
  )
    return;

  const unit = paymentDetail.unit || getUnitType(type);
  const formattedValue = formatValue(value, unit);
  const product = getProductFromNameOrType(name, type);

  if (!product || !product.paymentDetailTranslation) {
    if (type) {
      if (type === 'PREMIUM_USAGES') {
        return I18n.t('PREMIUM_USAGE_DETAIL', { value: formatValue(value, 'MONETARY') });
      }
      const rlhRoamingZone = extractRoamingZone(type);
      if (rlhRoamingZone) {
        switch (rlhRoamingZone) {
          case 'ROAM_LIKE_HOME2':
            return I18n.t('RoamingOffer.ExtendedRoamLikeHome.offerName');
          case 'ROAM_LIKE_HOME':
            return I18n.t('RoamingOffer.RoamLikeHome.offerName');
          default:
            return I18n.t('RoamingOffer.RoamingPackage.offerName', { zone: rlhRoamingZone });
        }
      }

      const iddZone = getBoosterZone(type, 'ZONE') || extractIddZone(type);
      if (iddZone) {
        if ('DURATION' === unit) {
          return I18n.t('IDD_VOICE_PAYMENT_DETAIL', { value: formatDuration(value), zone: iddZone });
        }
        if ('NUMBER' === unit) {
          return I18n.t('IDD_SMS_PAYMENT_DETAIL', { value: formatSms(value), zone: iddZone });
        }
      }

      const roamingZone = getBoosterZone(type, 'ROA');
      if (roamingZone) {
        switch (unit) {
          case 'DURATION':
            return I18n.t('ROA_VOICE_PAYMENT_DETAIL', { value: formatDuration(value), zone: roamingZone });
          case 'NUMBER':
            return I18n.t('ROA_SMS_PAYMENT_DETAIL', { value: formatSms(value), zone: roamingZone });
          case 'VOLUME':
            return I18n.t('ROA_DATA_PAYMENT_DETAIL', { value: formatVolume(value), zone: roamingZone });
        }
      }
    }
    if (name && name.includes('GSTREBATE')) {
      return I18n.t('GSTREBATE_PAYMENT_DETAIL');
    }

    if (I18n.has(`catalog.${name}_PAYMENT_DETAIL`)) {
      return I18n.t(`catalog.${name}_PAYMENT_DETAIL`, { value: formattedValue });
    }

    if (I18n.has(`catalog.${type}_PAYMENT_DETAIL`)) {
      return I18n.t(`catalog.${type}_PAYMENT_DETAIL`, { value: formattedValue });
    }

    if ('DATA_AND_EUROPE_CREDIT' === type) {
      return I18n.t(`catalog.DATA_AND_EUROPE_DETAIL`, { value: formattedValue });
    }

    if (!I18n.has(`catalog.${name}`) && !I18n.has(`catalog.${type}`)) {
      WizzeeLog.warn(`Missing ${type} && ${name} product ?`);
    }

    return I18n.has(`catalog.${name}`)
      ? I18n.t(`catalog.${name}`, { value: formattedValue })
      : I18n.t(`catalog.${type}`, { value: formattedValue });
  }

  if (isNaN(value)) {
    return product.unlimitedPaymentDetailTranslation
      ? I18n.t(product.unlimitedPaymentDetailTranslation)
      : I18n.t(product.paymentDetailTranslation);
  }

  if (!product.paymentDetailTranslation) {
    WizzeeLog.warn(`Missing ${type} product payment detail translation ?`);
    return '';
  }

  if (value === '0') {
    return '';
  }

  return I18n.t(product.paymentDetailTranslation, { value: formattedValue });
};

export const getPaymentLabelFromPaidItems = (paidItems: Object[]) => {
  const simProductRegex = /.*_SIM$/;
  const eSimProductRegex = /ESIM$/;
  const planProductRegex = /.*_PLN$/;
  const topUpIddRegex = /TO_ZONE[A-Z]+_.*$/;
  const topUpRoamingRegex = /ROA[1-9]+_.*$/;

  let paymentType = '';

  paidItems.forEach(({ name, type }) => {
    const isPremium = type === 'PREMIUM_USAGES';
    const isSim = simProductRegex.test(name);
    const isEsim = eSimProductRegex.test(name);
    const isPlan = planProductRegex.test(name);
    if (isPremium || isSim || isEsim || isPlan) {
      paymentType = name;
    }

    const isTopUp = name && name.includes('TOP');
    if (isTopUp) {
      paymentType = 'BOOSTER';
    }
    if (topUpIddRegex.test(type)) {
      paymentType = 'IDD';
    }
    if (topUpRoamingRegex.test(type)) {
      paymentType = 'ROAMING';
    }
  });

  return I18n.t(`Dashboard.Payments.history.label.${paymentType}`, { defaultValue: '' });
};
